import { CameraView, useCameraPermissions } from 'expo-camera';
import { router } from 'expo-router';
import { useRef, useState } from 'react';
import { ActivityIndicator, Dimensions, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CameraIcon,
    FootnoteRegular,
    H2Emphasized,
    Icon,
    isWeb,
    LabelRegular,
    ScreenContainer,
    View,
    YStack,
} from 'smoothie';

import type { CheckImage, CheckSide } from '../../types/CheckDeposit';

interface CheckDepositCameraProps {
    side: CheckSide;
    onCapture: (image: CheckImage) => void;
    onCancel: () => void;
}

const CheckDepositCamera = ({
    side,
    onCapture,
    onCancel,
}: CheckDepositCameraProps) => {
    const [permission, requestPermission] = useCameraPermissions();
    const [isCapturing, setIsCapturing] = useState(false);
    const cameraRef = useRef<CameraView>(null);
    const { top, bottom } = useSafeAreaInsets();

    const capture = async () => {
        if (!cameraRef.current || isCapturing) return;
        setIsCapturing(true);
        try {
            const photo = await cameraRef.current.takePictureAsync({
                quality: 0.9,
            });
            onCapture({
                uri: photo.uri,
                width: photo.width,
                height: photo.height,
            });
        } finally {
            setIsCapturing(false);
        }
    };

    if (!permission?.granted) {
        return (
            <ScreenContainer
                hasCloseButton
                onClose={() => {
                    router.navigate('/home/banking');
                }}
                containerProps={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: isWeb ? 'flex-start' : 'space-between',
                }}
            >
                <YStack
                    justifyContent={isWeb ? 'flex-start' : 'space-between'}
                    width="100%"
                    height="100%"
                    alignSelf="center"
                >
                    <YStack
                        alignItems="center"
                        marginTop={isWeb ? 50 : '30%'}
                        marginBottom={isWeb ? 100 : 0}
                        paddingHorizontal={24}
                    >
                        <View
                            alignSelf="center"
                            padding={25}
                            borderRadius={20}
                            backgroundColor="$gray200"
                            marginTop={30}
                            marginBottom={30}
                        >
                            <Icon icon={CameraIcon} size={30} />
                        </View>
                        <H2Emphasized textAlign="center">
                            Camera access required
                        </H2Emphasized>
                        <BodyMediumRegular
                            textAlign="center"
                            color="$gray1000"
                            marginTop={8}
                            marginHorizontal={12}
                        >
                            Camera access is required to capture your check for
                            mobile deposit.
                        </BodyMediumRegular>
                    </YStack>

                    <View paddingHorizontal={isWeb ? 24 : 0}>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Allow camera access"
                            onPress={requestPermission}
                            disabled={!permission}
                        />
                    </View>
                </YStack>
            </ScreenContainer>
        );
    }

    return (
        <View style={{ flex: 1, backgroundColor: 'black' }}>
            <CameraView
                ref={cameraRef}
                style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                }}
                facing="back"
            />

            {/* Check guide overlay */}
            <View
                style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 16,
                }}
            >
                <View
                    style={{
                        position: 'absolute',
                        width: FRAME_W,
                        height: FRAME_H,
                        borderWidth: 2,
                        borderColor: 'white',
                        borderRadius: 8,
                        backgroundColor: 'transparent',
                        transform: [{ rotate: '90deg' }],
                    }}
                />
                <FootnoteRegular
                    color="white"
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        top: SCREEN_H / 2 - VISUAL_H / 2 - 32,
                    }}
                >
                    {side === 'front' ? 'Front' : 'Back'}
                </FootnoteRegular>
            </View>

            {/* Top bar */}
            <Pressable
                onPress={onCancel}
                hitSlop={12}
                style={{ position: 'absolute', left: 20, top: top + 16 }}
            >
                <LabelRegular color="white">Cancel</LabelRegular>
            </Pressable>

            {/* Bottom controls */}
            <View
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    alignItems: 'center',
                    gap: 16,
                    paddingBottom: bottom + 24,
                }}
            >
                <Pressable
                    onPress={capture}
                    disabled={isCapturing}
                    style={({ pressed }) => ({
                        width: 72,
                        height: 72,
                        borderRadius: 36,
                        borderWidth: 4,
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        backgroundColor: 'white',
                        justifyContent: 'center' as const,
                        alignItems: 'center' as const,
                        ...(pressed && {
                            opacity: 0.7,
                            transform: [{ scale: 0.95 }],
                        }),
                    })}
                >
                    {isCapturing ? (
                        <ActivityIndicator size="small" color="black" />
                    ) : (
                        <View
                            style={{
                                width: 56,
                                height: 56,
                                borderRadius: 28,
                                backgroundColor: 'white',
                                borderWidth: 2,
                                borderColor: '$gray500',
                            }}
                        />
                    )}
                </Pressable>
            </View>
        </View>
    );
};

/**
 * After rotating 90°, visual width = original height, visual height = original width.
 * Define what we want the frame to look like on screen, then derive the pre-rotation dims.
 */
const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window');
const VISUAL_W = SCREEN_W * 0.72; // how wide the rotated frame appears
const VISUAL_H = SCREEN_H * 0.62; // how tall the rotated frame appears
/* Pre-rotation dimensions (swapped, since rotation swaps them visually) */
const FRAME_W = VISUAL_H; // becomes visual height after rotation
const FRAME_H = VISUAL_W; // becomes visual width after rotation

export default CheckDepositCamera;
